import React from 'react';
import { clsx } from 'clsx';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { paidDiscrepancy } from '../utils/paid-discrepancy';

/** The settlement facts PaidSummary needs — a structural subset of the list
 * row, which has carried `paid_by` since LR-557 (the strip is its only UI
 * surface now that the detail modal is gone). */
export interface PaidFacts {
  status: string;
  currency: string;
  total_accrued: string;
  paid_at: string | null;
  paid_amount: string | null;
  paid_by: string | null;
}

/** Paid summary strip (LR-556): settled date, actual amount, who, and the
 * discrepancy vs the accrued figure when there is one. */
export const PaidSummary: React.FC<{ data: PaidFacts }> = ({ data }) => {
  if (data.status !== 'paid') return null;
  const diff = paidDiscrepancy(data.total_accrued, data.paid_amount);
  // toDecimalString: consumption-boundary guard (HON-03) — decimal fields are
  // typed string but can arrive as numbers at runtime.
  const isReceivable = !(toDecimalString(data.total_accrued) ?? '').startsWith('-');
  const paidNegative = (toDecimalString(data.paid_amount) ?? '').startsWith('-');
  return (
    // Mock net-box palette for the settled state (#ecf6ef / #cde9d6).
    <div className="mb-4 rounded-md border border-[#cde9d6] bg-[#ecf6ef] px-3.5 py-2.5 text-sm text-gray-800">
      <span className="font-semibold text-green-700">Paid</span>
      {data.paid_at && <> · settled {data.paid_at.slice(0, 10)}</>}
      {data.paid_amount && (
        <>
          {' '}
          ·{' '}
          {/* Signed money color by the amount's own sign, matching the list
              column: a negative paid_amount must not read green. */}
          <span
            className={clsx(
              'font-semibold tabular-nums',
              paidNegative ? 'text-red-600' : 'text-green-700',
            )}
          >
            {formatDecimalString(data.paid_amount, { currency: data.currency })}
          </span>{' '}
          {isReceivable ? 'received' : 'paid'}
        </>
      )}
      {data.paid_by && (
        <> · by <span className="text-gray-500">{data.paid_by}</span></>
      )}
      {diff && (
        <div className="mt-1 text-xs text-amber-700">
          Differs from accrued by{' '}
          <span className="font-semibold tabular-nums">
            {formatDecimalString(diff, { currency: data.currency })}
          </span>
        </div>
      )}
    </div>
  );
};
